import { app } from "./configs/Firebase";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import {
  onValue,
  ref,
  get,
  update,
  onDisconnect,
  set,
} from "firebase/database";
import { db } from "./configs/Firebase";
import { Routes, Route, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { login } from "./features/statusSlice";
import { add } from "./features/orgsSlice";
import { setOrg } from "./features/orgSlice";

import "./App.css";
import SignIn from "./pages/SignIn";
import SignOut from "./pages/SignOut";
import Main from "./Main";
import Access from "./pages/access/Access";
import useVersionCheck from "./hooks/useVersionCheck";
import VersionUpdateToast from "./components/VersionUpdateToast";

const App = () => {
  const dispatch = useDispatch();
  const [showApp, setShowApp] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [FBUser, setFBUser] = useState(false);
  const [userData, setUserData] = useState(false);
  const orgsStatus = useSelector((state) => state.orgsStatus.value);

  // Version checking - only check when user is authenticated
  const { newVersionAvailable, currentVersion, resetVersionCheck } = useVersionCheck({
    checkInterval: 5 * 60 * 1000, // Check every 5 minutes
    onNewVersion: (newVersion, oldVersion) => {
      console.log(`New version detected: ${oldVersion} -> ${newVersion}`);
    }
  });

  const handleVersionDismiss = () => {
    resetVersionCheck();
  };

  useEffect(() => {
    const auth = getAuth(app);
    onAuthStateChanged(auth, (tuser) => {
      if (tuser) {
        const uid = tuser.uid;
        setShowApp(true);
        setFBUser(tuser);
        // console.log(tuser);

        onValue(
          ref(db, "userData/" + tuser.uid),
          (snapshot) => {
            if (!snapshot.exists()) {
              if (tuser.uid) {
                // Create initial profile - displayName may be null from Firebase Auth
                // It will be populated by fetchMicrosoftProfileData() after sign-in
                // Use specific paths to avoid overwriting profile data that might be set by fetchMicrosoftProfileData
                const profileUpdates = {
                  ["userData/" + tuser.uid + "/profile/role"]: "viewer",
                  ["userData/" + tuser.uid + "/profile/email"]: tuser.email,
                };
                // Only set displayName if it exists from Firebase Auth
                // Otherwise, it will be fetched from Microsoft Graph API
                if (tuser.displayName) {
                  profileUpdates["userData/" + tuser.uid + "/profile/displayName"] = tuser.displayName;
                }
                const dbRef = ref(db);
                update(dbRef, profileUpdates);
              }
            } else {
              let data = snapshot.val();
              // console.log("[App.js] Profile data from Firebase:", {
              //   hasProfile: !!data.profile,
              //   profileDisplayName: data.profile?.displayName,
              //   tuserDisplayName: tuser.displayName,
              //   fullProfile: data.profile
              // });
              
              // Check if existing user is missing displayName and update if needed
              // This handles users who signed in before displayName fetching was implemented
              if (data.profile && !data.profile.displayName && tuser.displayName) {
                // console.log("[App.js] Updating displayName from Firebase Auth:", tuser.displayName);
                update(ref(db, "userData/" + tuser.uid + "/profile"), {
                  displayName: tuser.displayName,
                });
              }
              // Ensure displayName is accessible at root level for components
              // Prefer profile.displayName (from database) over tuser.displayName (from Firebase Auth)
              const displayName = data.profile?.displayName || tuser.displayName || null;
              // console.log("[App.js] Setting userData with displayName:", displayName);
              setUserData({
                profile: data.profile,
                linked: data.linked,
                displayName: displayName,
                ...tuser,
              });
            }
          },
          (error) => {
            console.log("error", error);
          }
        );

      } else {
        setShowApp(false);
        setFBUser(false);
        if (location.pathname !== "/auth") {
          navigate("/auth");
        }
      }
    });
  }, []);

  const [linked, setLinked] = useState([]);
  useEffect(() => {
    // console.log(FBUser);
    if (FBUser && userData) {
      let tempUser = {
        ...userData,
        ...FBUser,
      };
      // console.log(tempUser);
      dispatch(login(JSON.stringify(tempUser)));

      var tempLinked = [];
      let selected = false;
      tempLinked[FBUser.uid] = {
        id: FBUser.uid,
        name: "Personal",
        read: true,
        write: true,
        linked: false,
      };

      for (let i in userData.linked) {
        get(ref(db, "userData/" + userData.linked[i].key + "/profile")).then(
          (snapshot) => {
            var tempTempLinked = [];
            if (orgsStatus) {
              // console.log();
              tempTempLinked = [...orgsStatus.payload];
            }
            let personalState = false;
            if (!userData.linked[i].selected) {
              personalState = true;
            }
            tempTempLinked[FBUser.uid] = {
              id: FBUser.uid,
              name: "Personal",
              read: true,
              write: true,
              linked: false,
              selected: personalState,
            };

            tempTempLinked[i] = {
              id: i,
              linked: true,
              ...userData.linked[i],
              ...snapshot.val(),
            };
            dispatch(add(tempTempLinked));
          }
        );

        if (userData.linked[i].selected) {
          selected = true;
          dispatch(setOrg(i));
        }
      }

      if (!selected) {
        tempLinked[Object.keys(tempLinked)[0]].selected = true;
        dispatch(setOrg(FBUser.uid));
      }
      dispatch(add(tempLinked));
    }
  }, [FBUser, userData]);

  return (
    <div className="App">
      <Routes>
        <Route path="/auth" element={<SignIn />} />
        <Route path="/sign-out" element={<SignOut />} />
        <Route path="/access/:id" element={<Access />} />
        <Route
          path="/*"
          element={<Main showApp={showApp} user={userData} />}
        ></Route>
      </Routes>
      {/* Version update notification - only show when user is authenticated */}
      {showApp && (
        <VersionUpdateToast
          show={newVersionAvailable}
          newVersion={currentVersion}
          onDismiss={handleVersionDismiss}
        />
      )}
    </div>
  );
};

export default App;
